import type { AdminShippingFeeSettings } from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useState } from 'react';
import { formatDateTime } from '@/shared/lib';
import { Button, FormField } from '@/shared/ui';
import {
  parseShippingFeeForm,
  SHIPPING_FEE_FIELDS,
  type ShippingFeeFormErrors,
  type ShippingFeeFormValues,
  shippingFeeFormFrom,
} from '../model/shipping-fee-form';
import { useUpdateShippingFeeSettings } from '../model/use-update-shipping-fee-settings';

export interface ShippingFeeSettingsFormProps {
  readonly settings: AdminShippingFeeSettings;
}

const NOTICE =
  '바꾼 금액은 새 견적 요청과 새로 채우는 배송비부터 적용됩니다. 이미 발행한 견적은 바뀌지 않습니다.';
const FIELD_TEXT: Readonly<
  Record<keyof ShippingFeeFormValues, { readonly label: string; readonly description: string }>
> = {
  baseFee: { label: '기본 배송비', description: '배송지 1곳마다 붙는 금액(원, 부가세 포함)' },
  jejuExtraFee: {
    label: '제주 추가',
    description: '제주 우편번호 배송지에 더하는 금액(원, 부가세 포함)',
  },
  remoteExtraFee: {
    label: '도서산간 추가',
    description: '제주 밖 도서산간 배송지에 더하는 금액(원, 부가세 포함)',
  },
};

/**
 * 배송비 설정 폼(SUPER-86 §6-2) — 세 금액, [저장], 마지막 변경 시각·변경자(R7: 이름, 없으면 '-'), 비소급 안내.
 * 저장 뒤 새 설정이 오면 상위가 key 로 다시 시작한다. 서버가 거부하면 입력을 그대로 두고 문구를 보인다.
 */
export const ShippingFeeSettingsForm = ({
  settings,
}: ShippingFeeSettingsFormProps): ReactElement => {
  const initial = shippingFeeFormFrom(settings);
  const [values, setValues] = useState<ShippingFeeFormValues>(initial);
  const [errors, setErrors] = useState<ShippingFeeFormErrors>({});
  const { update, isPending, error } = useUpdateShippingFeeSettings();
  const isDirty = SHIPPING_FEE_FIELDS.some((field) => values[field].trim() !== initial[field]);
  const updatedAt = settings.updatedAt ? formatDateTime(settings.updatedAt) : '-';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const parsed = parseShippingFeeForm(values);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    setErrors({});
    await update(parsed.input);
  };

  return (
    <form
      aria-label="배송비 설정"
      noValidate
      onSubmit={(event) => void handleSubmit(event)}
      className="flex max-w-xl flex-col gap-4"
    >
      {SHIPPING_FEE_FIELDS.map((field) => (
        <FormField
          key={field}
          label={FIELD_TEXT[field].label}
          description={FIELD_TEXT[field].description}
          inputMode="numeric"
          value={values[field]}
          error={errors[field]}
          disabled={isPending}
          onChange={(event) => setValues({ ...values, [field]: event.target.value })}
        />
      ))}
      {errors.total ? <p className="text-sm text-destructive">{errors.total}</p> : null}
      <p className="text-sm text-muted-foreground">{NOTICE}</p>
      <p className="text-sm text-muted-foreground">
        {`마지막 변경 ${updatedAt} · ${settings.updatedBy?.name ?? '-'}`}
      </p>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <Button type="submit" isLoading={isPending} disabled={!isDirty} className="self-start">
        저장
      </Button>
    </form>
  );
};
